import './manual-assignment-v4.css';
import './vehicles-of-the-day.css';

import moment from 'moment';
import React, { useEffect, useState } from 'react';

import {
  datesBetween,
  MAX_UNAVAILABILITY_DAYS,
} from './vehicles-of-the-day.utils';

/** Longueur maximale du motif, celle de vehicule_unavailabilities.reason. */
const REASON_MAX_LENGTH = 255;

/**
 * Confirmation « Marquer indisponible ce jour » (alignée sur
 * unavailable-modal.js de tms-chargeur), avec une fin facultative pour poser
 * plusieurs jours d'un coup.
 *
 * Un camion qui porte déjà des commandes ce jour les garde : la modale le dit
 * avant, l'écran le montre après, et c'est le dispatcher qui les déplace ou
 * les renvoie au puits.
 *
 * @param {object} props.vehicle - Le camion ({ key, licensePlate })
 * @param {string} props.date - La date affichée (YYYY-MM-DD)
 * @param {number} props.assignedCount - Les commandes qu'il porte ce jour
 * @param {Function} props.onConfirm - (dates, reason) => Promise<boolean> : vrai ferme la modale
 */
export default function UnavailableModal({
  vehicle,
  date,
  assignedCount = 0,
  onClose,
  onConfirm,
}) {
  const [reason, setReason] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setReason('');
    setEndDate('');
  }, [vehicle?.key, date]);

  if (!vehicle) return null;

  const dates = datesBetween(date, endDate && endDate > date ? endDate : null);
  const maxDate = moment(date, 'YYYY-MM-DD')
    .add(MAX_UNAVAILABILITY_DAYS - 1, 'days')
    .format('YYYY-MM-DD');
  const label =
    vehicle.licensePlate && vehicle.licensePlate !== vehicle.key
      ? `${vehicle.key} · ${vehicle.licensePlate}`
      : vehicle.key;
  const period =
    dates.length > 1
      ? `du ${moment(dates[0]).format('ddd D MMM')} au ${moment(
          dates[dates.length - 1]
        ).format('ddd D MMM YYYY')} (${dates.length} jours)`
      : moment(date, 'YYYY-MM-DD').format('ddd D MMM YYYY');

  const confirm = async () => {
    setIsSaving(true);
    try {
      if (await onConfirm(dates, reason.trim() || null)) onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mam-overlay" onClick={() => !isSaving && onClose()}>
      <div
        className="mam vod-modal"
        role="dialog"
        aria-label={`Marquer ${vehicle.key} indisponible`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mam__head">
          <div className="mam__titles">
            <h2 className="mam__title">Marquer {label} indisponible</h2>
            <p className="mam__summary">
              {period} · le camion reste disponible les autres jours
            </p>
          </div>
          <button
            type="button"
            className="mam__close"
            onClick={onClose}
            disabled={isSaving}
            title="Fermer"
          >
            ✕
          </button>
        </div>

        <div className="mam__body">
          <label className="vod-field">
            <span className="mam__block-title">
              MOTIF <span className="vod-field__optional">(facultatif)</span>
            </span>
            <input
              type="text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={REASON_MAX_LENGTH}
              placeholder="Panne, congés, contrôle technique…"
              disabled={isSaving}
              autoFocus
            />
          </label>

          <label className="vod-field">
            <span className="mam__block-title">
              JUSQU&apos;AU{' '}
              <span className="vod-field__optional">
                (facultatif, inclus — {MAX_UNAVAILABILITY_DAYS} jours au plus)
              </span>
            </span>
            <input
              type="date"
              value={endDate}
              min={date}
              max={maxDate}
              onChange={(event) => setEndDate(event.target.value)}
              disabled={isSaving}
            />
          </label>

          <p
            className={`vod-notice ${
              assignedCount > 0 ? 'vod-notice--warning' : ''
            }`}
          >
            <span aria-hidden="true">{assignedCount > 0 ? '⚠' : 'ℹ'}</span>
            <span>
              {assignedCount > 0
                ? `${vehicle.key} porte ${assignedCount} commande${
                    assignedCount > 1 ? 's' : ''
                  } ce jour. ${
                    assignedCount > 1
                      ? 'Elles ne sont pas retirées'
                      : 'Elle n’est pas retirée'
                  } : déplacez-${assignedCount > 1 ? 'les' : 'la'} ou renvoyez-${
                    assignedCount > 1 ? 'les' : 'la'
                  } au puits. ${vehicle.key} ne sera plus proposé à l'affectation ni à l'optimisation.`
                : `${vehicle.key} ne sera proposé ni à l'affectation ni à l'optimisation.`}
            </span>
          </p>
        </div>

        <div className="mam__foot">
          <span className="mam__spacer" />
          <button
            type="button"
            className="mam__btn"
            onClick={onClose}
            disabled={isSaving}
          >
            Annuler
          </button>
          <button
            type="button"
            className="mam__btn mam__btn--primary"
            onClick={confirm}
            disabled={isSaving}
          >
            {isSaving ? 'Enregistrement…' : 'Marquer indisponible'}
          </button>
        </div>
      </div>
    </div>
  );
}
